import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
    ShoppingCart, Search, Filter, Star, Package, Truck, Store,
    Heart, Plus, Minus, ChevronRight, Tag, CheckCircle2, X,
    ShoppingBag, MapPin, CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketplacePage = () => {
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [showCart, setShowCart] = useState(false);
    const [showCheckout, setShowCheckout] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    // Filters
    const [filters, setFilters] = useState({
        category: '',
        sport: '',
        search: '',
        minPrice: '',
        maxPrice: ''
    });

    const api = useCallback(() => {
        return axios.create({
            baseURL: `${API_URL}/api/marketplace`,
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
    }, [token]);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.category) params.append('category', filters.category);
            if (filters.sport) params.append('sport', filters.sport);
            if (filters.search) params.append('search', filters.search);
            if (filters.minPrice) params.append('min_price', filters.minPrice);
            if (filters.maxPrice) params.append('max_price', filters.maxPrice);
            
            const res = await axios.get(`${API_URL}/api/marketplace/products?${params}`);
            setProducts(res.data || []);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/marketplace/categories`);
            setCategories(res.data?.categories || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchCart = useCallback(async () => {
        if (!token) return;
        try {
            const res = await api().get('/cart');
            setCart(res.data || { items: [], total: 0 });
        } catch (error) {
            console.error('Error fetching cart:', error);
        }
    }, [token, api]);

    useEffect(() => {
        fetchCategories();
        fetchProducts();
        if (token) fetchCart();
    }, [fetchProducts, fetchCart, token]);

    const addToCart = async (productId, quantity = 1) => {
        if (!token) {
            toast.error('Please login to add items to cart');
            navigate('/auth');
            return;
        }
        
        try {
            await api().post(`/cart/add?product_id=${productId}&quantity=${quantity}`);
            toast.success('Added to cart!');
            fetchCart();
        } catch (error) {
            toast.error('Failed to add to cart');
        }
    };

    const updateCartItem = async (productId, quantity) => {
        try {
            await api().put(`/cart/update?product_id=${productId}&quantity=${quantity}`);
            fetchCart();
        } catch (error) {
            toast.error('Failed to update cart');
        }
    };

    const removeFromCart = async (productId) => {
        try {
            await api().delete(`/cart/item/${productId}`);
            fetchCart();
            toast.success('Removed from cart');
        } catch (error) {
            toast.error('Failed to remove item');
        }
    };

    const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

    return (
        <div className="min-h-screen bg-[#0a1628]">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-500 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white uppercase tracking-tight">
                                Marketplace
                            </h1>
                            <p className="text-white/80 mt-2">
                                Sports equipment, apparel & training services
                            </p>
                        </div>
                        
                        {/* Cart Button */}
                        <Button
                            onClick={() => setShowCart(true)}
                            className="relative bg-white text-black hover:bg-gray-100"
                        >
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Cart
                            {cartItemCount > 0 && (
                                <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">
                                    {cartItemCount}
                                </span>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search products..."
                            value={filters.search}
                            onChange={(e) => setFilters({...filters, search: e.target.value})}
                            className="pl-10 bg-[#0d2847] border-white/10"
                        />
                    </div>
                    
                    <Select 
                        value={filters.category} 
                        onValueChange={(v) => setFilters({...filters, category: v === 'all' ? '' : v})}
                    >
                        <SelectTrigger className="w-[180px] bg-[#0d2847] border-white/10">
                            <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10">
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>
                                    {cat.icon} {cat.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Button 
                        variant="outline" 
                        onClick={fetchProducts}
                        className="border-white/10"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Apply Filters
                    </Button>
                </div>
            </div>

            {/* Products Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="bg-[#0d2847] h-80 animate-pulse" />
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-20">
                        <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2">No Products Found</h3>
                        <p className="text-muted-foreground">
                            Try adjusting your filters or search terms
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {products.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onAddToCart={() => addToCart(product.id)}
                                onViewDetails={() => setSelectedProduct(product)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Cart Drawer */}
            <Dialog open={showCart} onOpenChange={setShowCart}>
                <DialogContent className="max-w-md bg-[#0a1628] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5 text-cyan-400" />
                            Shopping Cart
                        </DialogTitle>
                    </DialogHeader>
                    
                    <CartContent
                        cart={cart}
                        onUpdateQuantity={updateCartItem}
                        onRemove={removeFromCart}
                        onCheckout={() => {
                            setShowCart(false);
                            setShowCheckout(true);
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Checkout Dialog */}
            <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
                <DialogContent className="max-w-lg bg-[#0a1628] border-white/10">
                    <DialogHeader>
                        <DialogTitle>Checkout</DialogTitle>
                    </DialogHeader>
                    
                    <CheckoutForm
                        cart={cart}
                        token={token}
                        onSuccess={() => {
                            setShowCheckout(false);
                            fetchCart();
                            toast.success('Order placed successfully!');
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Product Details Dialog */}
            <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
                <DialogContent className="max-w-2xl bg-[#0a1628] border-white/10 max-h-[90vh] overflow-y-auto">
                    {selectedProduct && (
                        <ProductDetails
                            product={selectedProduct}
                            token={token}
                            onAddToCart={(qty) => {
                                addToCart(selectedProduct.id, qty);
                                setSelectedProduct(null);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Product Card Component
const ProductCard = ({ product, onAddToCart, onViewDetails }) => (
    <div className="bg-[#0d2847] border border-white/10 overflow-hidden group hover:border-cyan-500/50 transition-colors">
        {/* Image */}
        <div 
            className="aspect-square bg-black/30 flex items-center justify-center cursor-pointer"
            onClick={onViewDetails}
        >
            {product.images?.[0] ? (
                <img 
                    src={product.images[0]} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
            ) : (
                <Package className="w-16 h-16 text-muted-foreground" />
            )}
        </div>

        {/* Details */}
        <div className="p-4">
            <div className="text-xs text-cyan-400 mb-1">{product.category}</div>
            <h3 className="font-bold mb-1 line-clamp-2">{product.name}</h3>
            <div className="text-sm text-muted-foreground mb-2">{product.supplier_name}</div>
            
            {/* Price */}
            <div className="flex items-center justify-between mb-3">
                <div>
                    {product.discount_price ? (
                        <>
                            <span className="text-lg font-bold text-cyan-400">
                                KES {product.discount_price?.toLocaleString()}
                            </span>
                            <span className="text-sm text-muted-foreground line-through ml-2">
                                KES {product.price?.toLocaleString()}
                            </span>
                        </>
                    ) : (
                        <span className="text-lg font-bold text-cyan-400">
                            KES {product.price?.toLocaleString()}
                        </span>
                    )}
                </div>
                {product.rating > 0 && (
                    <div className="flex items-center gap-1 text-yellow-400">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm">{product.rating}</span>
                    </div>
                )}
            </div>

            <Button
                onClick={onAddToCart}
                className="w-full bg-primary hover:bg-cyan-400 text-black"
                disabled={!product.is_available}
            >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {product.is_available ? 'Add to Cart' : 'Out of Stock'}
            </Button>
        </div>
    </div>
);

// Cart Content Component
const CartContent = ({ cart, onUpdateQuantity, onRemove, onCheckout }) => (
    <div className="space-y-4">
        {cart.items?.length === 0 ? (
            <div className="text-center py-8">
                <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Your cart is empty</p>
            </div>
        ) : (
            <>
                <div className="max-h-80 overflow-y-auto space-y-3">
                    {cart.items?.map(item => (
                        <div key={item.product_id} className="flex gap-3 p-3 bg-[#0d2847] border border-white/10">
                            {item.image_url ? (
                                <img src={item.image_url} alt="" className="w-16 h-16 object-cover" />
                            ) : (
                                <div className="w-16 h-16 bg-black/30 flex items-center justify-center">
                                    <Package className="w-6 h-6 text-muted-foreground" />
                                </div>
                            )}
                            <div className="flex-1">
                                <div className="font-medium text-sm line-clamp-1">{item.product_name}</div>
                                <div className="text-xs text-muted-foreground">{item.supplier_name}</div>
                                <div className="text-cyan-400 font-bold mt-1">
                                    KES {(item.price * item.quantity).toLocaleString()}
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <button
                                    onClick={() => onRemove(item.product_id)}
                                    className="text-muted-foreground hover:text-red-400"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => onUpdateQuantity(item.product_id, item.quantity - 1)}
                                        className="w-6 h-6 bg-white/10 flex items-center justify-center hover:bg-white/20"
                                    >
                                        <Minus className="w-3 h-3" />
                                    </button>
                                    <span className="w-6 text-center">{item.quantity}</span>
                                    <button
                                        onClick={() => onUpdateQuantity(item.product_id, item.quantity + 1)}
                                        className="w-6 h-6 bg-white/10 flex items-center justify-center hover:bg-white/20"
                                    >
                                        <Plus className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Totals */}
                <div className="border-t border-white/10 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>KES {cart.subtotal?.toLocaleString()}</span>
                    </div>
                    {cart.delivery_charges > 0 && (
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Delivery</span>
                            <span>KES {cart.delivery_charges?.toLocaleString()}</span>
                        </div>
                    )}
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10">
                        <span>Total</span>
                        <span className="text-cyan-400">KES {cart.total?.toLocaleString()}</span>
                    </div>
                </div>

                <Button
                    onClick={onCheckout}
                    className="w-full bg-primary hover:bg-cyan-400 text-black"
                >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Proceed to Checkout
                </Button>
            </>
        )}
    </div>
);

// Checkout Form Component
const CheckoutForm = ({ cart, token, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        fulfillment_type: 'delivery',
        delivery_address: '',
        delivery_city: '',
        delivery_notes: '',
        payment_method: 'stripe'
    });

    const api = axios.create({
        baseURL: `${API_URL}/api/marketplace`,
        headers: { Authorization: `Bearer ${token}` }
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await api.post('/orders', {
                items: cart.items,
                ...form
            });
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to place order');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* Fulfillment Type */}
            <div className="space-y-2">
                <label className="text-sm font-medium">Fulfillment Type</label>
                <div className="flex gap-3">
                    <Button
                        type="button"
                        variant={form.fulfillment_type === 'delivery' ? 'default' : 'outline'}
                        onClick={() => setForm({...form, fulfillment_type: 'delivery'})}
                        className={form.fulfillment_type === 'delivery' ? 'bg-cyan-500' : 'border-white/10'}
                    >
                        <Truck className="w-4 h-4 mr-2" />
                        Delivery
                    </Button>
                    <Button
                        type="button"
                        variant={form.fulfillment_type === 'pickup' ? 'default' : 'outline'}
                        onClick={() => setForm({...form, fulfillment_type: 'pickup'})}
                        className={form.fulfillment_type === 'pickup' ? 'bg-cyan-500' : 'border-white/10'}
                    >
                        <Store className="w-4 h-4 mr-2" />
                        Pickup
                    </Button>
                </div>
            </div>

            {form.fulfillment_type === 'delivery' && (
                <>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Delivery Address</label>
                        <Input
                            value={form.delivery_address}
                            onChange={(e) => setForm({...form, delivery_address: e.target.value})}
                            placeholder="Street address"
                            className="bg-[#0d2847] border-white/10"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">City</label>
                        <Input
                            value={form.delivery_city}
                            onChange={(e) => setForm({...form, delivery_city: e.target.value})}
                            placeholder="City"
                            className="bg-[#0d2847] border-white/10"
                            required
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Delivery Notes (Optional)</label>
                        <Input
                            value={form.delivery_notes}
                            onChange={(e) => setForm({...form, delivery_notes: e.target.value})}
                            placeholder="Any special instructions"
                            className="bg-[#0d2847] border-white/10"
                        />
                    </div>
                </>
            )}

            {/* Order Summary */}
            <div className="bg-[#0d2847] p-4 border border-white/10">
                <h4 className="font-bold mb-3">Order Summary</h4>
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">{cart.items?.length} items</span>
                        <span>KES {cart.subtotal?.toLocaleString()}</span>
                    </div>
                    {cart.delivery_charges > 0 && (
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Delivery</span>
                            <span>KES {cart.delivery_charges?.toLocaleString()}</span>
                        </div>
                    )}
                    <div className="flex justify-between font-bold pt-2 border-t border-white/10">
                        <span>Total</span>
                        <span className="text-cyan-400">KES {cart.total?.toLocaleString()}</span>
                    </div>
                </div>
            </div>

            <Button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-400 text-black"
            >
                {loading ? 'Processing...' : 'Place Order'}
            </Button>
        </form>
    );
};

// Product Details Component
const ProductDetails = ({ product, onAddToCart, token }) => {
    const [quantity, setQuantity] = useState(1);
    const [showReviews, setShowReviews] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState(null);
    const [loadingReviews, setLoadingReviews] = useState(false);
    const [newReview, setNewReview] = useState({ rating: 5, title: '', comment: '' });
    const [submittingReview, setSubmittingReview] = useState(false);

    const fetchReviews = async () => {
        setLoadingReviews(true);
        try {
            const res = await axios.get(`${API_URL}/api/marketplace/products/${product.id}/reviews`);
            setReviews(res.data.reviews || []);
            setReviewStats(res.data);
        } catch (error) {
            console.error('Error fetching reviews:', error);
        } finally {
            setLoadingReviews(false);
        }
    };

    useEffect(() => {
        if (showReviews && reviews.length === 0) {
            fetchReviews();
        }
    }, [showReviews, product.id]);

    const submitReview = async () => {
        if (!token) {
            toast.error('Please login to submit a review');
            return;
        }
        setSubmittingReview(true);
        try {
            await axios.post(
                `${API_URL}/api/marketplace/products/${product.id}/reviews`,
                {
                    product_id: product.id,
                    rating: newReview.rating,
                    title: newReview.title,
                    comment: newReview.comment
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Review submitted successfully!');
            setNewReview({ rating: 5, title: '', comment: '' });
            fetchReviews();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit review');
        } finally {
            setSubmittingReview(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Image */}
                <div className="aspect-square bg-black/30 flex items-center justify-center">
                    {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                        <Package className="w-24 h-24 text-muted-foreground" />
                    )}
                </div>

                {/* Details */}
                <div className="space-y-4">
                    <div>
                        <span className="text-cyan-400 text-sm">{product.category}</span>
                        <h2 className="text-2xl font-bold mt-1">{product.name}</h2>
                        <div className="text-muted-foreground">{product.supplier_name}</div>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-2">
                        <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map(star => (
                                <Star
                                    key={star}
                                    className={`w-4 h-4 ${star <= Math.round(product.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-500'}`}
                                />
                            ))}
                        </div>
                        <span className="text-sm text-muted-foreground">
                            {product.rating?.toFixed(1) || '0.0'} ({product.reviews_count || 0} reviews)
                        </span>
                    </div>

                    <div className="flex items-center gap-3">
                        {product.discount_price ? (
                            <>
                                <span className="text-3xl font-bold text-cyan-400">
                                    KES {product.discount_price?.toLocaleString()}
                                </span>
                                <span className="text-xl text-muted-foreground line-through">
                                    KES {product.price?.toLocaleString()}
                                </span>
                                <span className="bg-red-500 text-white text-xs px-2 py-1">
                                    {Math.round((1 - product.discount_price / product.price) * 100)}% OFF
                                </span>
                            </>
                        ) : (
                            <span className="text-3xl font-bold text-cyan-400">
                                KES {product.price?.toLocaleString()}
                            </span>
                        )}
                    </div>

                    <p className="text-muted-foreground">{product.description}</p>

                    {product.sports?.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                            {product.sports.map(sport => (
                                <span key={sport} className="bg-[#0d2847] px-2 py-1 text-xs">
                                    {sport}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Quantity */}
                    <div className="flex items-center gap-4">
                        <span className="text-sm">Quantity:</span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                className="w-8 h-8 bg-white/10 flex items-center justify-center hover:bg-white/20"
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-12 text-center font-bold">{quantity}</span>
                            <button
                                onClick={() => setQuantity(quantity + 1)}
                                className="w-8 h-8 bg-white/10 flex items-center justify-center hover:bg-white/20"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <Button
                        onClick={() => onAddToCart(quantity)}
                        className="w-full bg-primary hover:bg-cyan-400 text-black h-12"
                        disabled={!product.is_available}
                    >
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Add to Cart - KES {((product.discount_price || product.price) * quantity).toLocaleString()}
                    </Button>
                </div>
            </div>

            {/* Reviews Section */}
            <div className="border-t border-white/10 pt-4">
                <button
                    onClick={() => setShowReviews(!showReviews)}
                    className="flex items-center justify-between w-full text-left"
                >
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-400" />
                        Customer Reviews ({product.reviews_count || 0})
                    </h3>
                    <ChevronRight className={`w-5 h-5 transition-transform ${showReviews ? 'rotate-90' : ''}`} />
                </button>

                {showReviews && (
                    <div className="mt-4 space-y-4">
                        {loadingReviews ? (
                            <div className="text-center py-4 text-muted-foreground">Loading reviews...</div>
                        ) : (
                            <>
                                {/* Rating Summary */}
                                {reviewStats && (
                                    <div className="grid grid-cols-2 gap-4 p-4 bg-[#0d2847]">
                                        <div className="text-center">
                                            <div className="text-4xl font-bold text-yellow-400">
                                                {reviewStats.average_rating?.toFixed(1) || '0.0'}
                                            </div>
                                            <div className="flex justify-center mt-1">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <Star
                                                        key={star}
                                                        className={`w-4 h-4 ${star <= Math.round(reviewStats.average_rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-500'}`}
                                                    />
                                                ))}
                                            </div>
                                            <div className="text-sm text-muted-foreground mt-1">
                                                {reviewStats.total} reviews
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            {[5, 4, 3, 2, 1].map(rating => (
                                                <div key={rating} className="flex items-center gap-2 text-xs">
                                                    <span>{rating}★</span>
                                                    <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-yellow-400"
                                                            style={{
                                                                width: `${reviewStats.total > 0 ? (reviewStats.rating_distribution[rating] / reviewStats.total * 100) : 0}%`
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="text-muted-foreground w-6">
                                                        {reviewStats.rating_distribution[rating]}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Write Review Form */}
                                {token && (
                                    <div className="p-4 bg-[#0d2847] space-y-3">
                                        <h4 className="font-bold">Write a Review</h4>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm">Rating:</span>
                                            <div className="flex">
                                                {[1, 2, 3, 4, 5].map(star => (
                                                    <button
                                                        key={star}
                                                        onClick={() => setNewReview(prev => ({ ...prev, rating: star }))}
                                                        className="p-1"
                                                    >
                                                        <Star
                                                            className={`w-6 h-6 ${star <= newReview.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-500'}`}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Review title (optional)"
                                            value={newReview.title}
                                            onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
                                            className="w-full bg-[#0a1628] border border-white/10 px-3 py-2 text-sm"
                                        />
                                        <textarea
                                            placeholder="Write your review..."
                                            value={newReview.comment}
                                            onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                                            rows={3}
                                            className="w-full bg-[#0a1628] border border-white/10 px-3 py-2 text-sm resize-none"
                                        />
                                        <Button
                                            onClick={submitReview}
                                            disabled={submittingReview}
                                            size="sm"
                                            className="bg-cyan-500 hover:bg-cyan-400 text-black"
                                        >
                                            {submittingReview ? 'Submitting...' : 'Submit Review'}
                                        </Button>
                                    </div>
                                )}

                                {/* Reviews List */}
                                {reviews.length === 0 ? (
                                    <div className="text-center py-4 text-muted-foreground">
                                        No reviews yet. Be the first to review!
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {reviews.map(review => (
                                            <div key={review.id} className="p-4 bg-[#0d2847] space-y-2">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">{review.user_name}</span>
                                                            {review.verified_purchase && (
                                                                <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 flex items-center gap-1">
                                                                    <CheckCircle2 className="w-3 h-3" /> Verified Purchase
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1 mt-1">
                                                            {[1, 2, 3, 4, 5].map(star => (
                                                                <Star
                                                                    key={star}
                                                                    className={`w-3 h-3 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-500'}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-muted-foreground">
                                                        {new Date(review.created_at).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {review.title && <h5 className="font-medium">{review.title}</h5>}
                                                {review.comment && <p className="text-sm text-muted-foreground">{review.comment}</p>}
                                                {review.helpful_count > 0 && (
                                                    <div className="text-xs text-muted-foreground">
                                                        {review.helpful_count} people found this helpful
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketplacePage;
